<template>
  <div>
    <div
      v-if="isOpenAdvSettings"
      class="autoincr-settings has-background-primary-light mb-2 p-3"
    >
      <button
        class="delete is-small is-pulled-right"
        @click="isOpenAdvSettings = !isOpenAdvSettings"
      ></button>
      <p class="help">
        Add a prefix and generate the next unique
        {{ $filters.lowercase(fieldLabel) }} for a serie of items within the
        given group.
      </p>
      <div class="autoincr-fields">
        <div class="autoincr-field">
          <label class="autoincr-label">Prefix (optional)</label>
          <div class="control" :class="{ 'is-loading': isIncrementing }">
            <input
              class="input"
              type="text"
              :placeholder="`e.g. ${fieldLabel.toLowerCase()}-prefix-`"
              :value="prefix"
              @input="handleChangePrefix"
              @keyup.enter="getNextValue"
            />
          </div>
        </div>
        <div class="autoincr-field">
          <label class="autoincr-label">Group</label>
          <div class="autoincr-group-row">
            <SelectContainer
              :value="selectedGroup"
              :options="userGroups"
              placeholder="Select group"
              @input="
                (g) => {
                  selectedGroup = g
                  getNextValue()
                }
              "
            />
            <button
              ref="autoIncrButton"
              v-tooltip="
                `Generate the next unique ${fieldLabel.toLowerCase()} for this item`
              "
              class="button is-small is-primary"
              :class="{
                'is-outlined': !!inputValue,
                'is-loading': isIncrementing,
              }"
              @click="getNextValue"
            >
              <span class="icon"><i class="fa-solid fa-step-forward" /></span>
              <span class="generate-label">Generate</span>
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="autoincr-value-row">
      <button
        v-if="!isOpenAdvSettings"
        ref="autoIncrButton"
        v-tooltip="
          `Generate the next unique ${fieldLabel.toLowerCase()} for this item`
        "
        class="button is-small is-primary"
        :class="{
          'is-outlined': !!inputValue,
          'is-loading': isIncrementing,
        }"
        @click="
          () => {
            openAdvancedSettings()
            getNextValue()
          }
        "
      >
        <span class="icon"><i class="fa-solid fa-step-forward" /></span>
      </button>
      <slot
        name="display-value"
        :is-loading="isIncrementing"
        :field-label="fieldLabel"
        :value="inputValue"
        :open-advanced-settings="openAdvancedSettings"
        :get-next-value="getNextValue"
      >
        <div class="field has-addons mb-0">
          <p class="control" :class="{ 'is-loading': isIncrementing }">
            <input
              v-model="inputValue"
              class="input"
              :class="{ 'is-success': copied }"
              type="text"
              @keyup.enter="getNextValue"
            />
          </p>
          <p class="control">
            <button
              v-tooltip="
                !isSupported
                  ? `Your browser does not support Clipboard API`
                  : `Copy ${fieldLabel}`
              "
              class="button"
              :class="{ 'is-success is-outlined': copied }"
              :disabled="!isSupported"
              @click="copy(inputValue)"
            >
              <span class="icon has-exponent-tag is-small">
                <i class="fa-regular fa-clipboard"></i>
              </span>
            </button>
          </p>
        </div>
        <span v-if="copied" class="help has-text-success">
          <span class="icon is-small">
            <i class="fa-solid fa-clipboard-check"></i>
          </span>
          {{ $filters.capitalize(fieldLabel) }} has been copied to clipboard
        </span>
      </slot>
    </div>
  </div>
</template>

<script>
import { useClipboard } from '@vueuse/core'
import { computed, onMounted, ref, watch } from 'vue'

import { useGetIncrementedValue } from '@/composables/fields/useGetIncrementedValue'
import { useStore } from '@/store'

import SelectContainer from '@/components/SelectContainer.vue'

export default {
  name: 'GetIncrementedValue',
  components: { SelectContainer },
  props: {
    fieldLabel: { type: String },
    url: { type: String, required: true },
    value: { type: [Number, String], default: '' },
    group: { type: Object, default: () => {} },
    showAdvancedSettings: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const store = useStore()
    const userGroups = computed(() => store.getters['auth/loggedInUser'].groups)

    const inputValue = ref(props.value)
    const selectedGroup = ref(props.group || null)
    const prefix = ref('')

    watch(
      () => props.value,
      (newValue) => (inputValue.value = newValue),
      { immediate: true },
    )
    watch(
      /* Update prefix on value prefix */
      () => inputValue.value,
      (v) => (prefix.value = v?.replace(/\d+$/, '') || ''),
      { immediate: true },
    )

    function handleChangePrefix(event) {
      prefix.value = event.target.value
    }

    const { isIncrementing, incrementValue } = useGetIncrementedValue(
      props.fieldLabel,
      props.url,
      selectedGroup,
      prefix,
    )

    const isOpenAdvSettings = ref(props.showAdvancedSettings)
    const openAdvancedSettings = () => (isOpenAdvSettings.value = true)
    watch(
      () => props.showAdvancedSettings,
      (isOp) => (isOpenAdvSettings.value = !!isOp),
      { immediate: true },
    )
    async function getNextValue() {
      const nextValue = await incrementValue()
      inputValue.value = `${prefix.value}${nextValue}`
      emit('next-value', inputValue.value)
    }

    const { copy, copied, isSupported } = useClipboard({ inputValue })

    onMounted(() => {
      if (isOpenAdvSettings.value && inputValue.value) getNextValue()
    })

    return {
      inputValue,
      emit,
      handleChangePrefix,
      isOpenAdvSettings,
      userGroups,
      selectedGroup,
      prefix,
      isIncrementing,
      getNextValue,
      openAdvancedSettings,
      /* Clipboard */
      copy,
      copied,
      isSupported,
    }
  },
}
</script>

<style scoped>
.autoincr-settings {
  border-radius: var(--radius-md);
  container-type: inline-size;
}

.autoincr-fields {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: var(--space-2);
  margin-top: var(--space-3);
  min-width: 0;
}

.autoincr-field {
  min-width: 0;
}

.autoincr-label {
  display: block;
  color: var(--text-muted);
  font-size: var(--text-sm);
  font-weight: var(--font-semibold);
  margin-bottom: var(--space-1);
}

.autoincr-group-row {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  min-width: 0;
}

.autoincr-group-row > :first-child {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.autoincr-group-row :deep(.multiselect) {
  min-width: 0;
}

.autoincr-group-row > button {
  flex-shrink: 0;
}

.autoincr-group-row > button .icon {
  margin-left: 0;
  margin-right: 0;
}

.generate-label {
  display: none;
  margin-left: var(--space-1);
}

/* Small container: stack everything */
@container (max-width: 350px) {
  .autoincr-fields {
    grid-template-columns: 1fr;
  }

  .autoincr-group-row {
    flex-wrap: wrap;
  }

  .autoincr-group-row > :first-child {
    flex-basis: 100%;
  }

  .autoincr-group-row > button {
    flex: 1;
  }

  .generate-label {
    display: inline;
  }
}

.autoincr-value-row {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  gap: var(--space-2);
  min-width: 0;
}

.autoincr-value-row > :last-child {
  min-width: 0;
}

.autoincr-value-row > .field {
  flex: 1;
  min-width: 0;
}
</style>
